import {getHeaders} from '../utils/requests';
import {SWFT_DIGITAL_API_HOST} from '../constants';

export function extractError(data: any, res: Response) {
    // if data instance of object, check message and title,
    // fallback to response.statusText
    if (typeof data !== 'object') {
        return res.statusText;
    }

    if (data.message) {
        return data.message;
    }
    if (data.detail) {
        return data.detail;
    }
    if (data.title) {
        return data.title;
    }
    if (res.status === 400) {
        // likely a validation error
        let message = '';
        if (Array.isArray(data)) {
            message += ' ' + data.join(', ');
            return message;
        }
        if (Object.keys(data).length > 0) {
            message += '. Please check the following fields:';
        }
        for (const key in data) {
            message += `\n${key}: `;
            if (Array.isArray(data[key])) {
                message += data[key].join(', ');
            } else {
                message += data[key];
            }
        }

        return message;
    }

    return res.statusText;
}

export type FetchResult<T> = {
    data?: T;
    error?: Error;
};

export function nFetch<T>(
    url: string,
    options?: RequestInit,
    json?: boolean,
    handle404?: boolean,
): Promise<FetchResult<T>> {
    json = json ?? true;
    handle404 = handle404 ?? true;

    options = options ?? {};
    options.headers = new Headers(options.headers);

    const defaultHeaders: {[key in string]: string} = getHeaders();
    for (const key in defaultHeaders) {
        options.headers.set(key, defaultHeaders[key]);
    }

    if (SWFT_DIGITAL_API_HOST) {
        if (!url.startsWith(SWFT_DIGITAL_API_HOST)) {
            url = SWFT_DIGITAL_API_HOST + url;
        }
    }

    return fetch(url, options)
        .then((res) => {
            let result: FetchResult<T> | undefined = undefined;
            if (!res.ok) {
                if (handle404 && res.status === 404) {
                    result = {
                        data: undefined,
                    };
                } else {
                    const json = res.headers.get('content-type')?.includes('application/json') ?? false;
                    if (json) {
                        return res
                            .json()
                            .then((data) => extractError(data, res))
                            .catch((err) => {
                                console.error('failed to decode json', err);
                                return res.statusText;
                            })
                            .then((message) => {
                                result = {
                                    error: new Error(message),
                                };
                                return {
                                    res,
                                    result,
                                };
                            });
                    }
                    result = {
                        error: new Error(res.statusText),
                    };
                }
            }
            return {
                res: res,
                result: result,
            };
        })
        .then(({res, result}) => {
            if (result != undefined) {
                return result;
            }
            if (json) {
                return res.json().then((data) => {
                    return {
                        data: data,
                    } as FetchResult<T>;
                });
            } else {
                return {
                    data: undefined,
                } as FetchResult<T>;
            }
        })
        .catch((err) => {
            let result: FetchResult<T> = {
                error: err,
            };
            return result;
        });
}
